import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

interface EmptyStateProps {
  /** Primary heading text */
  title: string;
  /** Optional description text below the title */
  description?: string;
  /** Optional icon to display above the title */
  icon?: ReactNode;
  /** Optional action button or link */
  action?: ReactNode;
  /** Additional CSS classes */
  className?: string;
}

/**
 * EmptyState displays a placeholder when content is not available.
 * Use for empty lists, search results, or initial states.
 *
 * @example
 * ```tsx
 * <EmptyState
 *   icon={<InboxIcon className="h-12 w-12" />}
 *   title="No messages"
 *   description="You don't have any messages yet."
 *   action={<Button>Send a message</Button>}
 * />
 * ```
 */
export function EmptyState({ title, description, icon, action, className }: EmptyStateProps) {
  return (
    <div
      className={cn('flex flex-col items-center justify-center py-12 px-4 text-center', className)}
      role="status"
      aria-label={title}
    >
      {icon !== undefined && <div className="mb-4 text-muted-foreground">{icon}</div>}
      <h3 className="text-lg font-semibold text-foreground">{title}</h3>
      {description !== undefined && (
        <p className="mt-2 max-w-sm text-sm text-muted-foreground">{description}</p>
      )}
      {action !== undefined && <div className="mt-6">{action}</div>}
    </div>
  );
}
